'use client';

import Link from 'next/link';
import { useMediaSource, type MediaVideoDetail } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { sectionPad, sectionInner } from '../_shared/styles';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Cinematic hero for the video module. Either pin a specific video
 * via {@code pinnedSlug}, or fetch the head of a source slug.
 */
export interface VideoModuleHeroProps {
  /** Pin a specific video by slug. */
  pinnedSlug?: string;
  /** When pinnedSlug isn't set, fetch the head of this source.
   *  Default {@code trending}. */
  source?: string;
  videoHrefTemplate?: string;
  ctaLabel?: string;
}

export function VideoModuleHero({
  pinnedSlug,
  source = 'trending',
  videoHrefTemplate = '/tube/{slug}',
  ctaLabel = 'Watch now',
}: VideoModuleHeroProps) {
  const media = useMediaSource();

  const state = useAsyncResource<MediaVideoDetail>(
    async (signal) => {
      if (!media) return null;
      if (pinnedSlug) return await media.videos.fetchBySlug(pinnedSlug, signal);
      const res = await media.videos.fetchSource({ source, limit: 1, signal });
      const item = res?.items[0];
      if (!item) return null;
      return await media.videos.fetchBySlug(item.slug, signal);
    },
    [media, pinnedSlug, source],
  );

  if (!media) {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <AdapterPlaceholder blockType="VideoModuleHero" adapterName="MediaSource" />
        </div>
      </BlockRoot>
    );
  }

  if (state.kind !== 'loaded') return null;

  const v = state.data;
  return (
    <BlockRoot as="section" style={sectionPad}>
      <div style={sectionInner}>
        <div
          style={{
            position: 'relative',
            aspectRatio: '21 / 9',
            borderRadius: 'var(--radius-xl, 24px)',
            overflow: 'hidden',
            background: v.posterUrl
              ? `linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0) 100%), url(${v.posterUrl}) center/cover no-repeat`
              : 'linear-gradient(135deg, var(--color-brand-500, #4f46e5), var(--color-brand-700, #312e81))',
            color: 'var(--color-text-on-brand, #fff)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              padding: 'clamp(20px, 4vw, 48px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {v.kicker && (
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: 'var(--font-tracking-widest, 0.18em)',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.85)',
                }}
              >
                {v.kicker}
              </div>
            )}
            <h2
              style={{
                margin: 0,
                fontSize: 'clamp(28px, 4vw, 44px)',
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: 'var(--font-tracking-snug, -0.02em)',
                color: 'var(--color-text-on-brand, #fff)',
                textShadow: '0 2px 12px rgba(0,0,0,0.3)',
                maxWidth: 800,
              }}
            >
              {v.title}
            </h2>
            {v.description && (
              <p
                style={{
                  margin: 0,
                  fontSize: 'clamp(14px, 1.5vw, 17px)',
                  lineHeight: 1.5,
                  color: 'rgba(255,255,255,0.85)',
                  maxWidth: 640,
                }}
              >
                {v.description}
              </p>
            )}
            <Link
              href={videoHrefTemplate.replace('{slug}', encodeURIComponent(v.slug))}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: 'var(--space-2-5, 10px) var(--space-6, 24px)',
                marginTop: 8,
                background: 'var(--color-neutral-0, #fff)',
                color: 'var(--color-text-primary, #18181b)',
                borderRadius: 999,
                fontSize: 'var(--font-size-sm, 14px)',
                fontWeight: 600,
                textDecoration: 'none',
                width: 'fit-content',
              }}
            >
              ▶ {ctaLabel}
            </Link>
          </div>
        </div>
      </div>
    </BlockRoot>
  );
}
